/**
 * App del Mesero (Atención de Piso, Notificaciones y Toma de Pedidos).
 *
 * Diseñada para smartphones en bolsillo o tablets de 8" del personal de piso.
 * Emite sonido y vibración cuando un platillo queda listo en cocina, permite marcar
 * la entrega con un toque y levantar comandas manuales en mesa.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../lib/api';
import { cantidad } from '../lib/formato';
import { reproducirTonoOrdenNueva } from '../lib/audio';
import { vibrarAvisoOrdenLista } from '../lib/vibration';
import { alRecibir } from '../lib/socket';

interface Mesa {
  id: number;
  numero: number;
  nombre: string;
  estado: 'libre' | 'ocupada' | 'por_cobrar';
  cuenta_activa_id: number | null;
  cuenta_total: number | null;
}

interface OrdenLista {
  id: number;
  numero_orden: number;
  estado: string;
  total: number | string;
  notas: string | null;
  creado_en: string;
  actualizado_en: string;
  mesa_numero: number | null;
  mesa_nombre: string | null;
  detalles: { id: number; cantidad: number | string; producto_nombre: string }[];
}

interface Categoria {
  id: number;
  nombre: string;
}

interface Producto {
  id: number;
  categoria_id: number | null;
  nombre: string;
  precio: number | string;
  estado: string;
}

interface ItemCarritoManual {
  producto: Producto;
  cantidad: number;
  notas: string;
  subtotal: number;
}

export function Mesero({ sucursalId }: { sucursalId: number }) {
  const [tab, setTab] = useState<'entregas' | 'mesas' | 'pedido'>('entregas');

  const [mesas, setMesas] = useState<Mesa[]>([]);
  const [ordenesListas, setOrdenesListas] = useState<OrdenLista[]>([]);
  const [cargando, setCargando] = useState(true);

  // Estados para toma de pedido manual
  const [mesaSeleccionada, setMesaSeleccionada] = useState<number | null>(null);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [catSel, setCatSel] = useState<number | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [carrito, setCarrito] = useState<ItemCarritoManual[]>([]);
  const [notasOrden, setNotasOrden] = useState('');
  const [enviando, setEnviando] = useState(false);

  const cargarDatos = useCallback(async () => {
    try {
      const [mRes, ordRes] = await Promise.all([
        api<Mesa[]>(`/mesas?sucursalId=${sucursalId}`),
        api<OrdenLista[]>(`/mesas/ordenes/listas?sucursalId=${sucursalId}`),
      ]);
      setMesas(mRes);
      setOrdenesListas(ordRes);
    } catch (err) {
      console.error('[mesero] Error al cargar datos:', err);
    } finally {
      setCargando(false);
    }
  }, [sucursalId]);

  useEffect(() => {
    cargarDatos();

    // Cargar catálogo para toma de pedido
    Promise.all([
      api<Categoria[]>(`/catalogo/categorias?sucursalId=${sucursalId}`),
      api<Producto[]>(`/catalogo/productos?sucursalId=${sucursalId}&estado=publicado&limite=500`),
    ]).then(([cRes, pRes]) => {
      setCategorias(cRes);
      setProductos(pRes);
    }).catch(() => null);

    // Escuchar eventos en tiempo real
    const unsubCambio = alRecibir('orden:cambio', (evt: any) => {
      if (evt?.estadoNuevo === 'lista') {
        reproducirTonoOrdenNueva();
        vibrarAvisoOrdenLista();
      }
      cargarDatos();
    });

    const unsubNueva = alRecibir('orden:nueva', () => {
      cargarDatos();
    });

    const unsubSinc = alRecibir('sincronizar', () => {
      cargarDatos();
    });

    return () => {
      unsubCambio();
      unsubNueva();
      unsubSinc();
    };
  }, [cargarDatos, sucursalId]);

  async function entregarOrden(ordenId: number) {
    try {
      await api(`/mesas/ordenes/${ordenId}/estado?sucursalId=${sucursalId}`, {
        method: 'PATCH',
        body: { estado: 'entregada' },
      });
      await cargarDatos();
    } catch (err: any) {
      alert(err?.message || 'No se pudo marcar la entrega.');
    }
  }

  function agregarAlCarrito(prod: Producto) {
    const precio = Number(prod.precio);
    setCarrito((prev) => {
      const idx = prev.findIndex((i) => i.producto.id === prod.id);
      if (idx >= 0) {
        const copia = [...prev];
        const cant = copia[idx].cantidad + 1;
        copia[idx] = { ...copia[idx], cantidad: cant, subtotal: Math.round(precio * cant * 100) / 100 };
        return copia;
      }
      return [...prev, { producto: prod, cantidad: 1, notas: '', subtotal: precio }];
    });
  }

  function cambiarCantidad(idx: number, delta: number) {
    setCarrito((prev) => {
      const copia = [...prev];
      const cant = copia[idx].cantidad + delta;
      if (cant <= 0) return prev.filter((_, i) => i !== idx);
      const precio = Number(copia[idx].producto.precio);
      copia[idx] = { ...copia[idx], cantidad: cant, subtotal: Math.round(precio * cant * 100) / 100 };
      return copia;
    });
  }

  const totalCarrito = useMemo(() => carrito.reduce((s, i) => s + i.subtotal, 0), [carrito]);

  async function enviarPedidoManual() {
    if (!mesaSeleccionada) {
      alert('Selecciona una mesa antes de enviar el pedido.');
      return;
    }
    if (carrito.length === 0 || enviando) return;

    try {
      setEnviando(true);
      const items = carrito.map((i) => ({
        productoId: i.producto.id,
        cantidad: i.cantidad,
        notas: i.notas || undefined,
      }));

      await api(`/mesas/ordenes/manual?sucursalId=${sucursalId}`, {
        method: 'POST',
        body: { mesaId: mesaSeleccionada, items, notas: notasOrden },
      });

      setCarrito([]);
      setNotasOrden('');
      setMesaSeleccionada(null);
      setTab('mesas');
      alert('¡Comanda enviada a la cocina!');
      await cargarDatos();
    } catch (err: any) {
      alert(err?.message || 'Error al enviar la comanda.');
    } finally {
      setEnviando(false);
    }
  }

  const prodsFiltrados = useMemo(() => {
    return productos.filter((p) => {
      const cCat = catSel === null || p.categoria_id === catSel;
      const q = busqueda.trim().toLowerCase();
      const cBus = !q || p.nombre.toLowerCase().includes(q);
      return cCat && cBus;
    });
  }, [productos, catSel, busqueda]);

  return (
    <div className="min-h-screen bg-cal pb-24 font-cuerpo text-tinta">
      {/* Cabecera */}
      <header className="sticky top-0 z-20 border-b border-salvia/20 bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto flex max-w-lg items-center justify-between">
          <h1 className="font-titulo text-xl font-extrabold text-nopal-oscuro">App del Mesero</h1>
          {ordenesListas.length > 0 && (
            <span className="flex items-center gap-1 rounded-full bg-nopal px-3 py-1 text-xs font-bold text-white animate-bounce">
              🔔 {ordenesListas.length} listas
            </span>
          )}
        </div>
      </header>

      {/* Contenido según Pestaña */}
      <main className="mx-auto max-w-lg px-4 pt-4">
        {cargando ? (
          <p className="py-12 text-center text-salvia">Cargando datos de piso…</p>
        ) : tab === 'entregas' ? (
          <div>
            <h2 className="font-titulo text-lg font-bold mb-3 text-tinta">
              Platillos Listos en Cocina ({ordenesListas.length})
            </h2>

            {ordenesListas.length === 0 ? (
              <div className="py-16 text-center text-salvia rounded-2xl border border-salvia/20 bg-white p-6">
                <div className="text-4xl">🍽️</div>
                <p className="mt-2 font-semibold">No hay comandas listas para entregar.</p>
                <p className="text-xs mt-1">Los avisos sonarán y vibrarán aquí al terminar la cocina.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {ordenesListas.map((ord) => (
                  <div
                    key={ord.id}
                    className="flex flex-col justify-between rounded-2xl border-2 border-nopal bg-white p-4 shadow-md"
                  >
                    <div>
                      <div className="flex items-center justify-between border-b border-salvia/15 pb-2">
                        <span className="font-titulo text-xl font-extrabold text-tinta">
                          {ord.mesa_nombre || `Mesa ${ord.mesa_numero || 'S/N'}`}
                        </span>
                        <span className="rounded-xl bg-nopal-claro px-2.5 py-1 text-xs font-bold text-nopal-oscuro">
                          Orden #{ord.numero_orden}
                        </span>
                      </div>

                      <div className="my-3 space-y-1">
                        {ord.detalles.map((d) => (
                          <div key={d.id} className="text-sm font-semibold">
                            <span className="font-bold text-nopal-oscuro">{cantidad(d.cantidad)}x </span>
                            <span>{d.producto_nombre}</span>
                          </div>
                        ))}
                      </div>

                      {ord.notas && (
                        <p className="text-xs italic text-salvia">Nota: "{ord.notas}"</p>
                      )}
                    </div>

                    <button
                      onClick={() => entregarOrden(ord.id)}
                      className="mt-3 w-full rounded-xl bg-nopal py-3 font-titulo text-base font-bold text-white transition active:scale-95 hover:bg-nopal-oscuro shadow-sm"
                    >
                      ✓ Marcar Entregado
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : tab === 'mesas' ? (
          <div>
            <h2 className="font-titulo text-lg font-bold mb-3 text-tinta">Estado del Piso</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {mesas.map((m) => (
                <div
                  key={m.id}
                  onClick={() => {
                    setMesaSeleccionada(m.id);
                    setTab('pedido');
                  }}
                  className={`flex flex-col justify-between rounded-2xl border p-4 cursor-pointer transition active:scale-95 shadow-sm ${
                    m.estado === 'ocupada'
                      ? 'border-miel bg-miel/10'
                      : m.estado === 'por_cobrar'
                      ? 'border-barro bg-barro/10'
                      : 'border-salvia/20 bg-white'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-titulo text-lg font-bold text-tinta">{m.nombre}</span>
                    <span
                      className={`h-2.5 w-2.5 rounded-full ${
                        m.estado === 'ocupada'
                          ? 'bg-miel'
                          : m.estado === 'por_cobrar'
                          ? 'bg-barro'
                          : 'bg-nopal'
                      }`}
                    />
                  </div>

                  <div className="mt-4 text-xs text-salvia">
                    {m.cuenta_activa_id ? (
                      <span className="font-bold text-nopal-oscuro">
                        ${Number(m.cuenta_total || 0).toFixed(2)}
                      </span>
                    ) : (
                      <span>Libre</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Pestaña Tomar Pedido Manual */
          <div>
            <div className="mb-4">
              <label className="mb-1 block text-xs font-bold text-salvia">Mesa a atender *</label>
              <select
                value={mesaSeleccionada || ''}
                onChange={(e) => setMesaSeleccionada(Number(e.target.value))}
                className="campo text-base"
              >
                <option value="">-- Selecciona una mesa --</option>
                {mesas.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.nombre} ({m.estado})
                  </option>
                ))}
              </select>
            </div>

            {/* Categorías */}
            <div className="flex overflow-x-auto pb-2 scrollbar-none">
              <button
                onClick={() => setCatSel(null)}
                className={`mr-2 whitespace-nowrap rounded-full px-3.5 py-1 text-xs font-bold ${
                  catSel === null ? 'bg-nopal text-white' : 'bg-white text-salvia'
                }`}
              >
                Todos
              </button>
              {categorias.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCatSel(c.id)}
                  className={`mr-2 whitespace-nowrap rounded-full px-3.5 py-1 text-xs font-bold ${
                    catSel === c.id ? 'bg-nopal text-white' : 'bg-white text-salvia'
                  }`}
                >
                  {c.nombre}
                </button>
              ))}
            </div>

            {/* Buscador */}
            <input
              type="search"
              placeholder="🔍 Buscar platillo…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="campo text-sm my-3"
            />

            {/* Grid de productos */}
            <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
              {prodsFiltrados.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between rounded-xl border border-salvia/20 bg-white p-3"
                >
                  <div>
                    <h4 className="font-titulo text-sm font-bold text-tinta">{p.nombre}</h4>
                    <span className="text-xs font-bold text-nopal-oscuro">
                      ${Number(p.precio).toFixed(2)}
                    </span>
                  </div>
                  <button
                    onClick={() => agregarAlCarrito(p)}
                    className="rounded-lg bg-nopal px-3 py-1.5 text-xs font-bold text-white active:scale-95"
                  >
                    + Agregar
                  </button>
                </div>
              ))}
            </div>

            {/* Resumen del carrito manual */}
            {carrito.length > 0 && (
              <div className="mt-4 rounded-2xl border border-nopal/30 bg-white p-4 shadow-md">
                <h3 className="font-titulo text-base font-bold text-tinta mb-2">Resumen de Comanda</h3>
                <div className="space-y-2 max-h-36 overflow-y-auto">
                  {carrito.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs border-b border-salvia/10 pb-1">
                      <span>{item.producto.nombre}</span>
                      <div className="flex items-center gap-2">
                        <button onClick={() => cambiarCantidad(idx, -1)} className="font-bold text-base px-1.5">-</button>
                        <span className="font-bold">{item.cantidad}</span>
                        <button onClick={() => cambiarCantidad(idx, 1)} className="font-bold text-base px-1.5">+</button>
                        <span className="font-bold text-nopal-oscuro ml-2">${item.subtotal.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3 flex items-center justify-between border-t border-salvia/15 pt-2">
                  <span className="font-bold text-salvia">Total</span>
                  <span className="font-titulo text-xl font-extrabold text-nopal-oscuro">
                    ${totalCarrito.toFixed(2)}
                  </span>
                </div>

                <button
                  disabled={enviando || !mesaSeleccionada}
                  onClick={enviarPedidoManual}
                  className="mt-3 w-full rounded-xl bg-nopal py-3 font-bold text-white disabled:opacity-50"
                >
                  {enviando ? 'Enviando…' : 'Enviar a Cocina'}
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Navegación Inferior Móvil */}
      <nav className="fixed bottom-0 inset-x-0 z-30 flex border-t border-salvia/20 bg-white py-2">
        <button
          onClick={() => setTab('entregas')}
          className={`flex-1 flex flex-col items-center justify-center text-xs font-bold py-1 ${
            tab === 'entregas' ? 'text-nopal-oscuro font-extrabold' : 'text-salvia'
          }`}
        >
          <span className="text-lg">🔔</span>
          <span>Entregas ({ordenesListas.length})</span>
        </button>

        <button
          onClick={() => setTab('mesas')}
          className={`flex-1 flex flex-col items-center justify-center text-xs font-bold py-1 ${
            tab === 'mesas' ? 'text-nopal-oscuro font-extrabold' : 'text-salvia'
          }`}
        >
          <span className="text-lg">🪑</span>
          <span>Mis Mesas</span>
        </button>

        <button
          onClick={() => setTab('pedido')}
          className={`flex-1 flex flex-col items-center justify-center text-xs font-bold py-1 ${
            tab === 'pedido' ? 'text-nopal-oscuro font-extrabold' : 'text-salvia'
          }`}
        >
          <span className="text-lg">📝</span>
          <span>Tomar Pedido</span>
        </button>
      </nav>
    </div>
  );
}
